/**
 * Hero Layout Utility Functions
 * 
 * Helper functions for parsing, validating, and manipulating hero layout values.
 * Also includes debouncing utilities for performance optimization.
 */

// Logging helper
function logInfo(message, ...args) {
  console.log(`[HERO_DEBUG] ${message}`, ...args);
}

/**
 * Creates a debounced version of a function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Milliseconds to wait
 * @returns {Function} - Debounced function
 */
export function debounce(func, wait = 300) {
  let timeout;
  
  const debounced = function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
  
  // Add cancel method to clear pending debounced call
  debounced.cancel = () => {
    clearTimeout(timeout);
  };
  
  return debounced;
}

/**
 * Parse a layout value from JSON string to array
 * @param {string} jsonString - JSON array string like "[1, 3]"
 * @returns {number[]|null} - Array of numbers or null if invalid
 */
export function parseLayoutValue(jsonString) {
  if (!jsonString) {
    return null;
  }

  // Handle array input (if backend returns parsed JSON)
  if (Array.isArray(jsonString)) {
    return jsonString;
  }

  if (typeof jsonString !== 'string') {
    logInfo('parseLayoutValue: Invalid type', typeof jsonString);
    return null;
  }

  try {
    const parsed = JSON.parse(jsonString);
    if (Array.isArray(parsed) && parsed.every(n => typeof n === 'number' && n > 0)) {
      return parsed;
    }
    logInfo('parseLayoutValue: Invalid array content', parsed);
    return null;
  } catch (e) {
    logInfo('parseLayoutValue: JSON parse error', e.message);
    return null;
  }
}

/**
 * Convert a layout array to JSON string
 * @param {number[]} array - Array of column widths
 * @returns {string} - JSON string like "[1, 3]"
 */
export function stringifyLayoutValue(array) {
  if (!Array.isArray(array)) {
    return '[6]';
  }

  // Filter out non-positive numbers
  const filtered = array.filter(n => typeof n === 'number' && n > 0);
  
  if (filtered.length === 0) {
    return '[6]';
  }

  return JSON.stringify(filtered);
}

/**
 * Validate a layout array
 * @param {number[]} array - Array of column widths
 * @returns {boolean} - True if valid
 */
export function validateLayoutArray(array) {
  if (!Array.isArray(array) || array.length === 0) {
    return false;
  }

  // All elements must be positive numbers
  return array.every(n => typeof n === 'number' && n > 0 && Number.isInteger(n));
}

/**
 * Get the default layout value
 * @returns {string} - Default JSON string "[6]"
 */
export function getDefaultLayout() {
  return '[6]';
}

/**
 * Normalize layout string (remove whitespace for comparison)
 * @param {string} layoutString - JSON array string
 * @returns {string} - Normalized string
 */
export function normalizeLayoutString(layoutString) {
  if (!layoutString) {
    return '';
  }
  
  // Handle array input
  if (Array.isArray(layoutString)) {
    return JSON.stringify(layoutString).replace(/\s/g, '');
  }

  if (typeof layoutString !== 'string') {
    return '';
  }
  return layoutString.replace(/\s/g, '');
}

/**
 * Compare two layout strings for equality
 * @param {string} layout1 - First layout string
 * @param {string} layout2 - Second layout string
 * @returns {boolean} - True if layouts are equivalent
 */
export function layoutsEqual(layout1, layout2) {
  return normalizeLayoutString(layout1) === normalizeLayoutString(layout2);
}

/**
 * Derive the layout mode from a hero layout value
 * Determines if all breakpoints are the same (one), or mobile/desktop split (two), or all different
 * 
 * @param {Object} value - Hero layout object with breakpoint keys
 * @param {string} value.extra_small
 * @param {string} value.small
 * @param {string} value.medium
 * @param {string} value.large
 * @param {string} value.extra_large
 * @returns {Object} - { mode: 'one'|'two'|'all', vMobile: string, vDesktop: string }
 */
export function deriveLayoutMode(value) {
  if (!value) {
    const defaultVal = getDefaultLayout();
    return {
      mode: 'one',
      vMobile: defaultVal,
      vDesktop: defaultVal
    };
  }

  const xs = value.extra_small || getDefaultLayout();
  const s = value.small || getDefaultLayout();
  const m = value.medium || getDefaultLayout();
  const l = value.large || getDefaultLayout();
  const xl = value.extra_large || getDefaultLayout();

  // Normalize for comparison
  const xsN = normalizeLayoutString(xs);
  const sN = normalizeLayoutString(s);
  const mN = normalizeLayoutString(m);
  const lN = normalizeLayoutString(l);
  const xlN = normalizeLayoutString(xl);

  // Check if all are the same (mode: one)
  if (xsN === sN && sN === mN && mN === lN && lN === xlN) {
    return {
      mode: 'one',
      vMobile: xs,
      vDesktop: m
    };
  }

  // Check if mobile (xs, s) matches and desktop (m, l, xl) matches (mode: two)
  if (xsN === sN && mN === lN && lN === xlN) {
    return {
      mode: 'two',
      vMobile: xs,
      vDesktop: m
    };
  }

  // Otherwise, all breakpoints are different (mode: all)
  return {
    mode: 'all',
    vMobile: xs,
    vDesktop: m
  };
}

/**
 * Apply a layout value based on mode
 * @param {Object} currentValue - Current hero layout object
 * @param {string} newLayoutValue - New layout string to apply
 * @param {string} mode - Current mode ('one', 'two', 'all')
 * @param {string} activeBP - Active breakpoint (only used in 'all' mode)
 * @returns {Object} - Updated hero layout object
 */
export function applyLayoutByMode(currentValue, newLayoutValue, mode, activeBP = null) {
  const current = currentValue || {};
  
  if (mode === 'one') {
    // Apply to all breakpoints
    return {
      ...current,
      extra_small: newLayoutValue,
      small: newLayoutValue,
      medium: newLayoutValue,
      large: newLayoutValue,
      extra_large: newLayoutValue
    };
  }

  if (mode === 'two') {
    // Determine if we're updating mobile or desktop
    // If activeBP is xs or s, update mobile; otherwise update desktop
    const isMobile = activeBP === 'extra_small' || activeBP === 'small' || !activeBP;
    
    if (isMobile) {
      return {
        ...current,
        extra_small: newLayoutValue,
        small: newLayoutValue
      };
    } else {
      return {
        ...current,
        medium: newLayoutValue,
        large: newLayoutValue,
        extra_large: newLayoutValue
      };
    }
  }

  // mode === 'all'
  if (!activeBP) {
    // Default to medium if no active breakpoint
    return {
      ...current,
      medium: newLayoutValue
    };
  }

  return {
    ...current,
    [activeBP]: newLayoutValue
  };
}

/**
 * Get the layout value for a specific breakpoint or mode
 * @param {Object} value - Hero layout object
 * @param {string} mode - Current mode
 * @param {string} activeBP - Active breakpoint
 * @returns {string} - Layout string for the current context
 */
export function getCurrentLayoutValue(value, mode, activeBP = 'medium') {
  if (!value) {
    return getDefaultLayout();
  }

  if (mode === 'one') {
    return value.extra_small || value.medium || getDefaultLayout();
  }

  if (mode === 'two') {
    const isMobile = activeBP === 'extra_small' || activeBP === 'small';
    return isMobile ? (value.extra_small || getDefaultLayout()) : (value.medium || getDefaultLayout());
  }

  // mode === 'all'
  return value[activeBP] || getDefaultLayout();
}
